// Converts a legacy ('v1') post/comment body to the v2 GFM-subset dialect.
// The fidelity contract is what the old renderer DISPLAYED, corruption
// included: the body is parsed with RichBodyRenderer's exact v1 semantics
// (cap newline runs, then global `**` split, then `*`, then `__`), and the
// resulting styled segments are serialized into the v2 dialect:
//
// - `**bold**` / `*italic*` stay, `__x__` becomes `<u>x</u>`;
// - empty mark pairs (the `**\n**` bold-across-break corruption) are
//   dropped, keeping their inner whitespace;
// - marks are re-opened across blank lines and edge whitespace moves
//   outside the markers, since GFM emphasis parses neither;
// - markdown metacharacters in plain text are escaped so v1 prose stays
//   literal; URL spans (as v1 linkified them) and `@handle` mentions pass
//   through untouched;
// - newline runs are capped at one blank line, matching displayed output.
//
// Only ever called on v1 input — callers gate on bodyFormat. A v2 body
// containing only text/bold/italic passes through unchanged, but bodies
// with `<u>`, lists, or backslash escapes would be re-escaped: this is a
// one-way conversion, not an idempotent one.

export function normalizeLegacyBody(body: string): string {
  const capped = body.replace(/\n{3,}/g, '\n\n');
  return serializeBold(capped).replace(/\n{3,}/g, '\n\n');
}

function serializeBold(text: string): string {
  return text
    .split('**')
    .map((part, i) => (i % 2 === 0 ? serializeItalic(part) : wrapMark(serializeItalic(part), part, '**', '**')))
    .join('');
}

function serializeItalic(text: string): string {
  return text
    .split('*')
    .map((part, i) => (i % 2 === 0 ? serializeUnderline(part) : wrapMark(serializeUnderline(part), part, '*', '*')))
    .join('');
}

function serializeUnderline(text: string): string {
  return text
    .split('__')
    .map((part, i) => (i % 2 === 0 ? escapeText(part) : wrapMark(escapeText(part), part, '<u>', '</u>')))
    .join('');
}

// Wraps a serialized segment in mark delimiters. `raw` is the segment's
// unserialized v1 content — emptiness (the dropped-pair rule) is judged on
// what the old renderer would have displayed, not on the escaped output.
function wrapMark(inner: string, raw: string, open: string, close: string): string {
  if (raw.trim() === '') return inner;
  return inner
    .split(/(\n{2,})/)
    .map((chunk) => {
      if (chunk.trim() === '') return chunk;
      const lead = /^\s*/.exec(chunk)![0];
      const trail = /\s*$/.exec(chunk.slice(lead.length))![0];
      const core = chunk.slice(lead.length, chunk.length - trail.length);
      return `${lead}${open}${core}${close}${trail}`;
    })
    .join('');
}

// Keep in lockstep with URL_RE in linkify-urls.tsx — v1 linkified exactly
// these spans, so exactly these spans must stay unescaped for GFM's
// bare-URL autolink to pick them up.
const URL_RE = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;

const META_CHAR_RE = /[\\`*_[\]<&~|]/g;

function escapeText(text: string): string {
  if (text === '') return text;
  let escaped: string;
  if (text.includes('http')) {
    const parts: string[] = [];
    let last = 0;
    let match: RegExpExecArray | null;
    URL_RE.lastIndex = 0;
    while ((match = URL_RE.exec(text)) !== null) {
      parts.push(escapeMetaChars(text.slice(last, match.index)), match[0]);
      last = match.index + match[0].length;
    }
    parts.push(escapeMetaChars(text.slice(last)));
    escaped = parts.join('');
  } else {
    escaped = escapeMetaChars(text);
  }
  return escapeLineStarts(escaped);
}

function escapeMetaChars(text: string): string {
  return text.replace(META_CHAR_RE, (ch) => `\\${ch}`);
}

// Characters that are only syntax at the start of a line: headings, quotes,
// bullets, setext underlines, and ordered-list markers.
function escapeLineStarts(text: string): string {
  return text
    .replace(/(^|\n)([ \t]*)([#>+=-])/g, '$1$2\\$3')
    .replace(/(^|\n)([ \t]*)(\d{1,9})([.)])(?=[ \t\n]|$)/g, '$1$2$3\\$4');
}
